import React, { useEffect, useRef, useState } from 'react';
import styles from './manual-date.module.css';
import moment from 'moment/moment';
import { User } from '../../types';
import { useTranslation } from 'react-i18next';

type ManualEndDateProps = {
  durationMonths: number;
  onDurationChange: (months: number) => void;
  onToggleManual: () => void;
  endDate: Date | null;
  startDate: Date | null;
  user: User | undefined;
  disabled: boolean;
};

export const ManualDate: React.FC<ManualEndDateProps> = ({
  durationMonths,
  onDurationChange,
  onToggleManual,
  endDate,
  startDate,
  user,
  disabled,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(durationMonths.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(durationMonths.toString());
  }, [durationMonths]);

  const handleBlur = () => {
    let value = parseInt(inputValue, 10);
    if (isNaN(value) || value < 1) {
      value = 1;
    } else if (value > 99) {
      value = 99;
    }
    onDurationChange(value);
    setInputValue(value.toString());
  };

  const handleManualMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,2}$/.test(value)) {
      setInputValue(value);
      const parsedValue = parseInt(value, 10);
      if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 99) {
        onDurationChange(parsedValue);
      }
    }
  };

  const handleButtonClick = (change: number) => {
    const newValue = Math.min(99, Math.max(1, durationMonths + change));
    onDurationChange(newValue);
    setInputValue(newValue.toString());
  };

  return (
    <div className={styles.durationContainer}>
      <div>
        {t('duration')}*
        <div className={styles.durationControls}>
          <button
            type="button"
            className={styles.durationButtonMinus}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleButtonClick(-1);
            }}
            disabled={
              durationMonths <= 1 ||
              (user &&
                moment(user.endAt).diff(moment(startDate), 'month') >=
                  moment(endDate).diff(moment(startDate), 'month'))
            }
          >
            −
          </button>
          <input
            ref={inputRef}
            type="text"
            min="1"
            max="99"
            value={inputValue}
            onChange={handleManualMonthChange}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            className={styles.durationInput}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={disabled}
          />
          <button
            type="button"
            className={styles.durationButtonPlus}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleButtonClick(1);
            }}
            disabled={durationMonths >= 99 || disabled}
          >
            +
          </button>
        </div>
      </div>
      {!disabled && (
        <div
          className={styles.endDateDisplay}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.endDateText}>{t('endDate')}:</div>
          <div
            className={styles.endDateValue}
            onClick={onToggleManual}
          >
            {endDate ? new Date(endDate).toLocaleDateString('en-GB') : '--/--/----'}
          </div>
        </div>
      )}
    </div>
  );
};
